import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getCurrentUser } from '../../data/mockData';
import AccountSidebar from '../../components/AccountSidebar';
import { request } from '../../utils/request';
import { 
  GiftIcon,
  ShareIcon,
  ClipboardDocumentIcon,
  QrCodeIcon,
  PlusIcon,
  ChartBarIcon,
  UserGroupIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';

// API Response Types
interface ReferralStatsResponse {
  success: boolean;
  code: number;
  message: string;
  result: {
    totalReferrals: number;
    activeReferrals: number;
    totalEarnings: number;
    thisMonthEarnings: number;
  };
}

interface ReferralCouponsResponse {
  success: boolean;
  code: number;
  message: string;
  result: {
    coupons: ReferralCoupon[];
    pagination: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  };
}

interface ReferralRecordsResponse {
  success: boolean;
  code: number;
  message: string;
  result: {
    records: ReferralRecord[];
    pagination: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  };
}

interface CreateCouponResponse {
  success: boolean;
  code: number;
  message: string;
  data: ReferralCoupon;
}

// Legacy interfaces for backward compatibility
interface ReferralStats {
  totalReferrals: number;
  activeReferrals: number;
  totalEarnings: number;
  thisMonthEarnings: number;
}

interface ReferralCoupon {
  id: number;
  code: string;
  discount: number;
  type: 'percentage' | 'fixed';
  usageCount: number;
  maxUsage: number;
  expiresAt: string;
  isActive: boolean;
  description: string;
  commissionRate: number;
  createdAt: string;
  updatedAt: string;
}

interface ReferralRecord {
  id: number;
  customerName: string;
  customerEmail: string;
  couponCode: string;
  orderAmount: number;
  commissionEarned: number;
  date: string;
  status: 'completed' | 'pending' | 'cancelled';
}

const Referrals: React.FC = () => {
  const [user] = useState(getCurrentUser());
  const [activeTab, setActiveTab] = useState<'overview' | 'coupons' | 'referrals' | 'create'>('overview');
  const [newCoupon, setNewCoupon] = useState({
    code: '',
    discount: '',
    type: 'percentage' as 'percentage' | 'fixed',
    maxUsage: '',
    expiresAt: '',
    description: ''
  });

  // API data states
  const [referralStats, setReferralStats] = useState<ReferralStats | null>(null);
  const [referralCoupons, setReferralCoupons] = useState<ReferralCoupon[]>([]);
  const [referralRecords, setReferralRecords] = useState<ReferralRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreatingCoupon, setIsCreatingCoupon] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    loadReferralStats();
    loadReferralCoupons();
    loadReferralRecords();

    if (activeTab === 'create') {
      const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
      const nums = '0123456789';
      let code = '';
      for (let i = 0; i < 2; i++) {
        code += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      for (let i = 0; i < 4; i++) {
        code += nums.charAt(Math.floor(Math.random() * nums.length));
      }
      setNewCoupon(prev => ({ ...prev, code }));
      
      // Set default expiration date to 7 days from now
      const date = new Date();
      date.setDate(date.getDate() + 7);
      const defaultDate = date.toISOString().split('T')[0];
      setNewCoupon(prev => ({ ...prev, expiresAt: defaultDate }));
    }
  }, [activeTab]);

  const loadReferralStats = async () => {
    try {
      const response = await request({
        method: 'GET',
        url: 'referral/stats'
      }) as ReferralStatsResponse;

      if (response.success) {
        setReferralStats(response.result);
      } else {
        setError(response.message || 'Failed to load referral stats');
      }
    } catch (err) {
      console.error('Error loading referral stats:', err);
      setError('Failed to load referral stats');
    }
  };

  const loadReferralCoupons = async () => {
    try {
      const response = await request({
        method: 'GET',
        url: 'referral/coupons'
      }) as ReferralCouponsResponse;

      if (response.success) {
        setReferralCoupons(response.result.coupons);
      } else {
        setError(response.message || 'Failed to load referral coupons');
      }
    } catch (err) {
      console.error('Error loading referral coupons:', err);
      setError('Failed to load referral coupons');
    }
  };

  const loadReferralRecords = async () => {
    try {
      const response = await request({
        method: 'GET',
        url: 'referral/records'
      }) as ReferralRecordsResponse;

      if (response.success) {
        setReferralRecords(response.result.records);
      } else {
        setError(response.message || 'Failed to load referral records');
      }
    } catch (err) {
      console.error('Error loading referral records:', err);
      setError('Failed to load referral records');
    } finally {
      setIsLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Please log in</h2>
          <Link to="/" className="hover:opacity-80" style={{ color: '#2e3a47' }}>
            Back to home
          </Link>
        </div>
      </div>
    );
  }

  // Use API data or fallback to defaults
  const stats: ReferralStats = referralStats || {
    totalReferrals: 0,
    activeReferrals: 0,
    totalEarnings: 0,
    thisMonthEarnings: 0
  };

  // Transform API data to match legacy format
  const transformedReferralRecords: ReferralRecord[] = referralRecords.map(record => ({
    id: record.id,
    customerName: record.customerName || 'Customer ***',
    customerEmail: record.customerEmail || '',
    couponCode: record.couponCode || '',
    orderAmount: record.orderAmount || 0,
    commissionEarned: record.commissionEarned || 0,
    date: record.date || new Date().toISOString().split('T')[0],
    status: record.status || 'pending'
  }));

  const handleCreateCoupon = async () => {
    if (newCoupon.code && newCoupon.discount && newCoupon.maxUsage) {
      try {
        setIsCreatingCoupon(true);
        setError(null);

        const response = await request({
          method: 'POST',
          url: 'referral/coupons',
          data: {
            code: newCoupon.code,
            discount: parseFloat(newCoupon.discount),
            type: newCoupon.type,
            maxUsage: parseInt(newCoupon.maxUsage),
            expiresAt: newCoupon.expiresAt || null,
            description: newCoupon.description
          }
        }) as CreateCouponResponse;

        if (response.success) {
          // Reset form
          setNewCoupon({
            code: '',
            discount: '',
            type: 'percentage',
            maxUsage: '',
            expiresAt: '',
            description: ''
          });
          // Reload coupons
          loadReferralCoupons();
          // Switch to coupons tab
          setActiveTab('coupons');
        } else {
          setError(response.message || 'Failed to create coupon');
        }
      } catch (err) {
        console.error('Error creating coupon:', err);
        setError('Failed to create coupon');
      } finally {
        setIsCreatingCoupon(false);
      }
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    // Show success message
  };

  const generateQRCode = (code: string) => {
    // Generate QR code for sharing
    console.log('Generate QR for:', code);
  };

  const shareCoupon = (code: string) => {
    // Share coupon via social media or messaging
    console.log('Sharing coupon:', code);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Referral Program</h1>
          <p className="text-gray-600">Create and manage referral coupons to earn commissions</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="hidden lg:block lg:col-span-1">
            <AccountSidebar currentPage="referrals" />
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Loading State */}
            {isLoading && (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderBottomColor: '#2e3a47' }}></div>
                <span className="ml-2 text-gray-600">Loading...</span>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <div className="flex">
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">Error</h3>
                    <div className="mt-2 text-sm text-red-700">
                      <p>{error}</p>
                    </div>
                    <div className="mt-4">
                      <button
                        onClick={() => {
                          setError(null);
                          loadReferralStats();
                          loadReferralCoupons();
                          loadReferralRecords();
                        }}
                        className="bg-red-100 px-3 py-2 rounded-md text-sm font-medium text-red-800 hover:bg-red-200"
                      >
                        Try Again
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Referrals</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalReferrals}</p>
            </div>
            <div className="p-3 rounded-full" style={{ backgroundColor: '#f0f2f5' }}>  
              <UserGroupIcon className="w-6 h-6" style={{ color: '#2e3a47' }} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Referrals</p>
              <p className="text-2xl font-bold text-green-600">{stats.activeReferrals}</p>
            </div>
            <div className="bg-green-100 p-3 rounded-full">
              <ChartBarIcon className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Earnings</p>
              <p className="text-2xl font-bold text-gray-900">${stats.totalEarnings.toFixed(2)}</p>
            </div>
            <div className="bg-yellow-100 p-3 rounded-full">
              <CurrencyDollarIcon className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">This Month</p>
              <p className="text-2xl font-bold text-purple-600">${stats.thisMonthEarnings.toFixed(2)}</p>
            </div>
            <div className="bg-purple-100 p-3 rounded-full">
              <GiftIcon className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {[
              { id: 'overview', name: 'Overview' },
              { id: 'coupons', name: 'My Coupons' },
              { id: 'referrals', name: 'Referral Records' },
              { id: 'create', name: 'Create Coupon' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                style={activeTab === tab.id ? { borderBottomColor: '#68aeb0', color: '#68aeb0' } : {}}
              >
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Recent Referrals */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Referrals</h3>
                  <div className="space-y-3">
                    {transformedReferralRecords.length > 0 ? (
                      transformedReferralRecords.slice(0, 3).map((record) => (
                        <div key={record.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <p className="font-medium text-gray-900">{record.customerName}</p>
                            <p className="text-sm text-gray-600">Used: {record.couponCode}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-medium text-green-600">+${Number(record.commissionEarned).toFixed(2)}</p>
                            <p className="text-xs text-gray-500">{record.date}</p>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <p>No referral records found</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Quick Actions */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
                  <div className="space-y-3">
                    <button
                      onClick={() => setActiveTab('create')}
                      className="w-full flex items-center justify-center px-4 py-3 text-white rounded-lg transition-colors"
                      style={{ backgroundColor: '#2e3a47' }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a4a57'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2e3a47'}
                    >
                      <PlusIcon className="w-5 h-5 mr-2" />
                      Create New Coupon
                    </button>
                    <button onClick={() => setActiveTab('coupons')} className="w-full flex items-center justify-center px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700">
                      <ShareIcon className="w-5 h-5 mr-2" />
                      Share Referral Link
                    </button>
                    <button className="w-full flex items-center justify-center px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
                      <ChartBarIcon className="w-5 h-5 mr-2" />
                      View Analytics
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Coupons Tab */}
          {activeTab === 'coupons' && (
            <div className="space-y-6">
              {referralCoupons.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {referralCoupons.map((coupon) => (
                    <div key={coupon.id} className="bg-white border border-gray-200 rounded-lg p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">{coupon.code}</h3>
                          <p className="text-sm text-gray-600">
                            {coupon.type === 'percentage' ? `${coupon.discount}% off` : `$${coupon.discount} off`}
                          </p>
                        </div>
                        <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                          coupon.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {coupon.isActive ? 'Active' : 'Inactive'}
                        </div>
                      </div>
                      
                      <div className="space-y-3">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Usage:</span>
                          <span className="font-medium">{coupon.usageCount}/{coupon.maxUsage}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Expires:</span>
                          <span className="font-medium">{coupon.expiresAt ? new Date(coupon.expiresAt).toLocaleDateString() : 'N/A'}</span>
                        </div>
                        
                        <div className="flex space-x-2 pt-3">
                          <button
                            onClick={() => copyToClipboard(coupon.code)}
                            className="flex-1 flex items-center justify-center px-3 py-2 text-white rounded-lg transition-colors text-sm"
                            style={{ backgroundColor: '#2e3a47' }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a4a57'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2e3a47'}
                          >
                            <ClipboardDocumentIcon className="w-4 h-4 mr-2" />
                            Copy
                          </button>
                          {/* <button
                            onClick={() => generateQRCode(coupon.code)}
                            className="flex-1 flex items-center justify-center px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm"
                          >
                            <QrCodeIcon className="w-4 h-4 mr-2" />
                            QR
                          </button> */}
                          <button className="flex-1 flex items-center justify-center px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm">
                            <ShareIcon className="w-4 h-4 mr-2" />
                            Share
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-gray-400 mb-4">
                    <GiftIcon className="w-12 h-12 mx-auto" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No referral coupons</h3>
                  <p className="text-gray-500">You haven't created any referral coupons yet.</p>
                  <button
                    onClick={() => setActiveTab('create')}
                    className="mt-4 text-white px-4 py-2 rounded-lg transition-colors"
                    style={{ backgroundColor: '#2e3a47' }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a4a57'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2e3a47'}
                  >
                    Create Your First Coupon
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Referral Records Tab */}
          {activeTab === 'referrals' && (
            <div>
              {transformedReferralRecords.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Coupon Used</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order Amount</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Commission</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {transformedReferralRecords.map((record) => (
                        <tr key={record.id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{record.customerName}</div>
                              <div className="text-sm text-gray-500">{record.customerEmail}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{record.couponCode}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">${Number(record.orderAmount).toFixed(2)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-medium">+${Number(record.commissionEarned).toFixed(2)}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              record.status === 'completed' ? 'bg-green-100 text-green-800' :
                              record.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {record.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{record.date}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-gray-400 mb-4">
                    <UserGroupIcon className="w-12 h-12 mx-auto" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No referral records</h3>
                  <p className="text-gray-500">You haven't received any referrals yet.</p>
                </div>
              )}
            </div>
          )}

          {/* Create Coupon Tab */}
          {activeTab === 'create' && (
            <div className="max-w-2xl">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Create New Referral Coupon</h3>
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Coupon Code (Auto Generated)</label>
                    <input
                      type="text"
                      value={newCoupon.code}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed text-gray-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Discount Type</label>
                    <select
                      value={newCoupon.type}
                      onChange={(e) => setNewCoupon({...newCoupon, type: e.target.value as 'percentage' | 'fixed'})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2"
                      style={{ '--tw-ring-color': '#2e3a47' } as React.CSSProperties}
                      onFocus={(e) => e.currentTarget.style.borderColor = '#2e3a47'}
                      onBlur={(e) => e.currentTarget.style.borderColor = ''}
                    >
                      <option value="percentage">Percentage (%)</option>
                      {/* <option value="fixed">Fixed Amount ($)</option> */}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Discount Value {newCoupon.type === 'percentage' && '(Max 20%)'}</label>
                    <input
                      type="number"
                      value={newCoupon.discount}
                      onChange={(e) => {
                        let val = parseFloat(e.target.value);
                        if (newCoupon.type === 'percentage' && val > 20) {
                          val = 20;
                        }
                        if (newCoupon.type === 'percentage' && val < 1) {
                          val = 1;
                        }
                        setNewCoupon({...newCoupon, discount: val.toString()})
                      }}
                      max={newCoupon.type === 'percentage' ? "20" : undefined}
                      placeholder={newCoupon.type === 'percentage' ? '20' : '10'}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2"
                      style={{ '--tw-ring-color': '#2e3a47' } as React.CSSProperties}
                      onFocus={(e) => e.currentTarget.style.borderColor = '#2e3a47'}
                      onBlur={(e) => e.currentTarget.style.borderColor = ''}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Max Usage (Max 10000)</label>
                    <input
                      type="number"
                      value={newCoupon.maxUsage}
                      onChange={(e) => {
                        let val = parseInt(e.target.value);
                        if (val > 10000) val = 10000;
                        setNewCoupon({...newCoupon, maxUsage: isNaN(val) ? '' : val.toString()})
                      }}
                      max="10000"
                      placeholder="100"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2"
                      style={{ '--tw-ring-color': '#2e3a47' } as React.CSSProperties}
                      onFocus={(e) => e.currentTarget.style.borderColor = '#2e3a47'}
                      onBlur={(e) => e.currentTarget.style.borderColor = ''}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Expiration Date</label>
                  <input
                    type="date"
                    value={newCoupon.expiresAt}
                    onChange={(e) => setNewCoupon({...newCoupon, expiresAt: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={newCoupon.description}
                    onChange={(e) => setNewCoupon({...newCoupon, description: e.target.value})}
                    placeholder="Describe the coupon purpose..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="flex justify-end space-x-4">
                  <button
                    onClick={() => setActiveTab('coupons')}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateCoupon}
                    disabled={!newCoupon.code || !newCoupon.discount || !newCoupon.maxUsage || isCreatingCoupon}
                    className="px-6 py-2 text-white rounded-lg transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                    style={{ backgroundColor: '#2e3a47' }}
                    onMouseEnter={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = '#3a4a57' }}
                    onMouseLeave={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = '#2e3a47' }}
                  >
                    {isCreatingCoupon ? 'Creating...' : 'Create Coupon'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Referrals; 